import { ORBI_SOFT_GUARDRAIL_TYPES } from '@patient-ui/shared/constants';

/**
 * Orbi guardrail helpers — pure functions that extract and classify the
 * guardrail indicator from an agent response. This is the DATA layer
 * (PCSA-13164); the locked-conversation experience that consumes it lives in
 * PCSA-13170. Mirrors mobile `orbi_self_harm_guardrail.dart` +
 * `_resolveGuardrailFields` (orbi_chat_service.dart).
 */

/**
 * Uppercases and strips whitespace / `-` / `_` so the gateway's varying shapes
 * (`SELFHARM`, `Self-Harm`, `self_harm`) normalize to a single token.
 */
export function normalizeOrbiGuardrailType(
  type?: string | null
): string | undefined {
  // Guard any falsy value (null / undefined / empty string): an empty type
  // carries no classification, so treat it the same as "absent".
  if (!type) {
    return undefined;
  }
  return type.toUpperCase().replace(/[\s_-]/g, '');
}

/**
 * True when the guardrail type is "soft" — surfaces a warning but does NOT lock
 * the chat (OFFTOPIC, FOREIGNLANGUAGE). Everything else that triggers
 * (EMERGENCY, HARM, SELFHARM, or any unknown type) is a hard lock.
 */
export function isOrbiSoftGuardrailType(type?: string | null): boolean {
  const normalized = normalizeOrbiGuardrailType(type);
  return normalized != null && ORBI_SOFT_GUARDRAIL_TYPES.includes(normalized);
}

/**
 * True when a triggered guardrail should lock the chat for the session — i.e.
 * it is triggered AND not a soft type. Mirrors mobile `orbiMessageLocksChat`.
 */
export function orbiMessageLocksChat(
  guardrailTriggered: boolean,
  type?: string | null
): boolean {
  return guardrailTriggered && !isOrbiSoftGuardrailType(type);
}

/**
 * Resolves the guardrail verdict from a response body, tolerating camelCase /
 * PascalCase / snake_case key shapes. Falls back to `agent === 'guardrail'`
 * for `triggered` and to `returnType` for `type` (mirrors mobile
 * `_resolveGuardrailFields`). `body` is the object that carries the nested
 * `guardrailResult` (the top-level response, or the decoded data map).
 */
export function resolveOrbiGuardrail(
  body: Record<string, unknown> | null | undefined,
  agent: string | undefined,
  returnType: string | undefined
): { triggered: boolean; type?: string } {
  const isGuardrailAgent = agent?.toLowerCase() === 'guardrail';

  const nestedRaw = body?.['guardrailResult'] ?? body?.['GuardrailResult'];
  const nested: Record<string, unknown> | undefined =
    nestedRaw && typeof nestedRaw === 'object'
      ? (nestedRaw as Record<string, unknown>)
      : undefined;

  const triggeredRaw =
    nested?.['guardrail_triggered'] ??
    nested?.['GuardrailTriggered'] ??
    nested?.['guardrailTriggered'];
  const triggered =
    typeof triggeredRaw === 'boolean' ? triggeredRaw : isGuardrailAgent;

  const typeRaw =
    nested?.['guardrail_type'] ??
    nested?.['GuardrailType'] ??
    nested?.['guardrailType'];
  // Prefer the nested guardrail type; otherwise, for a guardrail agent, fall
  // back to the response's returnType (mirrors mobile `_resolveGuardrailFields`).
  let type: string | undefined;
  if (typeof typeRaw === 'string') {
    type = typeRaw;
  } else if (isGuardrailAgent) {
    type = returnType;
  }

  return { triggered, type };
}
